<template>
  <transition name="fade" mode="out-in">
    <div v-if="isMiniCartOpen" :class="{'mini-cart': true, 'active': isMiniCartOpen}">
      <div class="mini-cart--background" @click="closeMiniCart"></div>
      <div class="mini-cart--content">
        <SloLoader :loading="cartAddingToCart || cartLoading" size="xl">
          <header class="mini-cart--header flex items-center justify-between w-100">
            <h3 v-if="cartTotalUnitCount" class="m-0 text-xl fw-strong c-blue-400 flex items-center">
              <SloIcon name="CircleCheckSolid" size="lg" class="mr-sm" />
              {{ 'Added to Cart!' | t }}
            </h3>
            <h3 v-else class="m-0 text-xl fw-strong c-blue-400 flex items-center">
              {{ 'Cart' | t }}
            </h3>
            <button class="mini-cart--close p-0 bg-transparent" @click="closeMiniCart">
              <SloIcon name="XMark" size="lg" />
            </button>
          </header>
          <div class="mini-cart--content-wrapper">
            <div class="mini-cart--body">
              <transition name="cart-product">
                <SloLoader key="minicart-loader" v-if="cartTotalUnitCount > 0" :loading="cartLoading">
                  <CartProduct
                    v-for="(cartEntry, cartEntryIndex) in cartEntries"
                    :key="`minicart-entry-${cartEntryIndex}`"
                    :entry-number="cartEntry?.entryNumber"
                    :quantity="cartEntry?.quantity"
                    :estimated-ship-date="cartEntry?.product?.estimatedShipDate"
                    :price-each="cartEntry?.product?.price?.formattedValue"
                    :price-each-subscription="cartEntry?.product?.subscriptionPrice?.formattedValue"
                    :total-price="cartEntry?.totalPrice?.formattedValue"
                    :title="cartEntry?.product?.number"
                    :description="cartEntry?.product?.title"
                    :uom="cartEntry?.product?.uom"
                    :sku="cartEntry?.product?.code"
                    :product-type="cartEntry?.product?.type"
                    :subscription-frequencies="cartEntry?.product?.subscriptionFrequencies"
                    :subscription-frequency="cartEntry?.subscriptionFrequency"
                    :subscription-percentage-discount="cartEntry?.product?.subscriptionPercentageDiscount"
                    @removeProduct="handleRemoveProduct(cartEntry?.entryNumber)"
                    @quantityChange="handleQuantityChange($event, cartEntry?.entryNumber, cartEntry?.quantity)"
                  >
                    <template v-slot:image>
                      <SloProductImage :source="cartEntry?.product?.images | getProductThumbnail" />
                    </template>
                  </CartProduct>
                </SloLoader>
                <div v-else class="mini-cart--empty flex items-center justify-center mt-md">
                  <p class="m-0 text-xl fw-bold">{{ 'Your cart is currently empty.' | t }}</p>
                </div>
              </transition>
            </div>
            <div v-if="cartTotalUnitCount > 0" class="mini-cart--middle flex items-center justify-between w-100">
              <SloButton @click="closeMiniCart" fill="outline" class="mini-cart--continue w-100">
                {{ 'Continue Shopping' | t }}
              </SloButton>
              <SloButton @click="navigateToCart" type="button" fill="solid" class="mini-cart--proceed w-100">
                {{ 'Proceed to Cart' | t }}
              </SloButton>
            </div>
            <footer v-if="cartReferences.length && cartTotalUnitCount > 0" class="mini-cart--footer">
              <SloLoader key="minicart-references-loader" :loading="cartLoading">
                <div class="mini-cart--footer-title">
                  <h3 class="text-lg fw-strong">{{ 'You might also like' | t }}</h3>
                </div>
                <div ref="cartReferencesCarousel" class="mini-cart--upsell-carousel">
                  <div class="mini-cart--upsell-carousel-wrapper">
                    <CartProduct
                      v-for="(cartReference, cartReferenceKey) in cartReferences"
                      :key="`minicart-reference-${cartReferenceKey}`"
                      :is-up-sell="true"
                      :title="cartReference?.target?.number"
                      :description="cartReference?.target?.title"
                      :sku="cartReference?.target?.code"
                      :product-type="cartReference?.target?.type"
                      :price-each="cartReference?.target?.price?.formattedValue"
                      :uom="cartReference?.target?.uom"
                      :estimated-ship-date="cartReference?.target?.estimatedShipDate"
                      class="up-sell"
                    >
                      <template v-slot:image>
                        <SloProductImage :source="cartReference?.target?.images | getProductThumbnail" />
                      </template>
                    </CartProduct>
                  </div>
                  <div class="mini-cart--carousel-dots">
                    <button
                      v-for="(snap, index) in emblaScrollSnaps"
                      :key="index" @click="embla.scrollTo(index)"
                      :class="index === emblaSelectedScrollSnap ? 'active' : ''"
                      class="mini-cart--carousel-dot"
                    ></button>
                  </div>
                  <button class="mini-cart--upsell-carousel-button button-prev" @click="embla.scrollPrev()">
                    <SloIcon name="ChevronLeft" />
                  </button>
                  <button class="mini-cart--upsell-carousel-button button-next" @click="embla.scrollNext()">
                    <SloIcon name="ChevronRight" />
                  </button>
                </div>
              </SloLoader>
            </footer>
          </div>
        </SloLoader>
      </div>
    </div>
  </transition>
</template>

<script>
import aa from 'search-insights';
import EmblaCarousel from 'embla-carousel';
import {mapActions, mapGetters, mapState} from 'vuex';
import algoliaConfig from '../../js/main/algolia-config';
import t from '../../js/main/i18n';
import {getProductThumbnail} from '../../js/main/utils';
import SloLoader from './SloLoader.vue';
import SloButton from './SloButton.vue';
import SloIcon from './SloIcon.vue';
import CartProduct from '../../js/marketplace/components/CartProduct.vue';
import SloProductImage from './SloProductImage.vue';

export default {
  name: 'SloMiniCart',
  filters: {
    t,
    getProductThumbnail
  },
  components: {
    SloLoader,
    SloButton,
    SloIcon,
    CartProduct,
    SloProductImage
  },
  data() {
    return {
      embla: null,
      emblaScrollSnaps: [],
      emblaSelectedScrollSnap: null
    };
  },
  created() {
    window.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.closeMiniCart();
    });
  },
  mounted() {
    if (this.user) {
      aa('init', {
        appId: algoliaConfig.appId,
        apiKey: algoliaConfig.apiKey,
        userToken: this.user?.customerId,
        authenticatedUserToken: this.user?.customerId
      });
    }
  },
  watch: {
    status(newValue) {
      if (this.isMiniCartOpen && newValue === 'refresh_token_failed') {
        window.location =
          `/marketplace/auth/login?sessionExpired=1&redirect=${window.location.pathname}${window.location.search}`;
      }
    },
    cartLoading(newValue) {
      if (!newValue && this.cartReferences) {
        setTimeout(() => this.initCartReferencesCarousel(), 500);
      }
    }
  },
  computed: {
    ...mapState('auth', ['status', 'user']),
    ...mapGetters('marketplace', ['cartEntries', 'cartTotalUnitCount']),
    ...mapState('marketplace', [
      'cartLoading', 'cartAddingToCart', 'isMiniCartOpen', 'cartId', 'cartReferences'
    ])
  },
  methods: {
    ...mapActions('marketplace', ['closeMiniCart', 'removeCartEntry', 'updateCartEntryQuantity']),
    navigateToCart() {
      this.closeMiniCart();
      window.location = '/marketplace/cart';
    },
    async handleRemoveProduct(entryNumber) {
      await this.removeCartEntry({entryNumber});
    },
    async handleQuantityChange(quantity, entryNumber, prevQuantity) {
      if (prevQuantity === quantity) return;
      await this.updateCartEntryQuantity({entryNumber, quantity});
    },
    initCartReferencesCarousel() {
      if (this.$refs.cartReferencesCarousel) {
        this.embla = EmblaCarousel(
          this.$refs.cartReferencesCarousel,
          {loop: true, draggable: true, containScroll: 'trimSnaps'}
        );
        this.embla.on('select', this.onEmblaDotSelect);
        this.emblaScrollSnaps = this.embla.scrollSnapList();
        this.onEmblaDotSelect();
      }
    },
    onEmblaDotSelect() {
      this.emblaSelectedScrollSnap = this.embla.selectedScrollSnap();
    }
  }
};
</script>

<style lang="scss">
@import '../../scss/system';

.mini-cart {
  position: fixed;
  inset: 0;
  z-index: 1100;
  isolation: isolate;
  width: 100%;
  height: 100vh;
  visibility: hidden;
  overflow: hidden;

  &.fade-enter-active {
    transition-duration: 0.325s;
    transition-timing-function: linear;

    .mini-cart--background {
      animation: fade 0.3s ease-in forwards;
    }

    .mini-cart--content {
      animation: slideIn 0.3s ease-in-out forwards;
    }
  }

  &.fade-leave-active {
    transition-duration: 0.325s;
    transition-timing-function: linear;
    transition-delay: 0.15s;

    .mini-cart--content {
      animation: slideOut 0.3s ease-in-out forwards;
    }
  }

  @keyframes fade {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0);
    }
  }

  @keyframes slideOut {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  &.fade-enter,
  &.fade-leave-to {
    opacity: 0;
  }

  &.active {
    visibility: visible;

    .mini-cart--background {
      opacity: 1;
    }

    .mini-cart--content {
      transform: translateX(0);
    }
  }

  &--background {
    position: fixed;
    top: 0;
    left: 0;
    z-index: -1;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &--close {
    display: inline-flex;
    width: 22px;
    height: 22px;

    .slo-icon.size-lg {
      width: 22px !important;
      height: 22px !important;
    }
  }

  &--empty {
    &.cart-product-enter-active {
      transition-property: transform, opacity;
      transition-duration: 0.5s;
      transition-timing-function: cubic-bezier(0.5, -0.4, 0.46, 1.41), cubic-bezier(0.25, 0.46, 0.45, 0.94);
      transition-delay: 0s, 0.2s;
    }

    &.cart-product-enter {
      transform: translateY(-100%);
      opacity: 0;
    }
  }

  &--content {
    width: 100%;
    max-width: 480px;
    height: 100%;
    overflow-y: scroll;
    margin-inline-start: auto;
    background-color: #fff;
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;

    &::-webkit-scrollbar {
      display: none;
    }

    .slo-loader {
      height: 100%;
    }
  }

  &--content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: space-between;
    height: calc(100% - 67px);
  }

  &--header, &--middle {
    padding-inline: 2.4rem;
  }

  &--header {
    position: sticky;
    top: 0;
    padding-block: 1.6rem;
    background-color: $color-white;
    border-bottom: 1px solid $color-gray-200;
    z-index: 100;
    overflow: hidden;

    .slide-fade-enter-active, .slide-fade-leave-active {
      transition-property: transform, opacity;
      transition-duration: 0.25s;
      transition-timing-function: ease;
    }

    .slide-fade-enter {
      transform: translateY(20%);
      opacity: 0;
    }

    .slide-fade-leave-to {
      transform: translateY(-20%);
      opacity: 0;
    }
  }

  &--body {
    width: 100%;

    @media (min-width: 768px) {
      height: 100%;
      overflow-y: scroll;
    }
  }

  &--middle {
    position: sticky;
    bottom: 0;
    flex-wrap: wrap;
    gap: 1.6rem;
    margin-block-start: auto;
    background-color: #fff;
    padding-block: 2.4rem;
    border-top: 1px solid $color-gray-400;

    > * {
      flex: 1;
    }
  }

  &--footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: start;
    gap: 2.4rem;
    width: 100%;
    background-color: $color-gray-200;
    padding-block: 2.4rem;

    @media (min-width: 768px) {
      position: sticky;
      bottom: 0;
    }

    .slo-loader {
      width: 100%;
    }

    & > div:not([class]) {
      padding-inline: 2.4rem;
    }
  }

  &--footer-title {
    padding-inline: 2.4rem;
  }

  &--upsell-carousel {
    display: block;
    position: relative;
    width: 100%;
    overflow: hidden;

    &-wrapper {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }
  }

  &--upsell-carousel-button {
    display: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    padding: 0;
    background-color: $color-white;
    color: $color-gray-700;
    border-radius: 9999px;
    opacity: 0.75;
    transition: color 0.15s ease-in, opacity 0.15s ease-in;

    &:hover {
      opacity: 1;
      color: $color-gray-900;
    }

    &.button-prev {
      left: 2.4rem;
    }

    &.button-next {
      right: 2.4rem;
    }

    @media (min-width: 768px) {
      display: block;
    }
  }

  &--carousel-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 0.8rem;
    margin-block-start: 1.6rem;

    @media (min-width: 768px) {
      display: none;
    }
  }

  &--carousel-dot {
    width: 8px;
    height: 8px;
    padding: 0;
    border-radius: 9999px;
    background-color: $color-gray-400;
    cursor: pointer;

    &.active {
      background-color: $color-blue-400;
    }
  }

  .slo-loader-icon {
    min-height: 195px;
  }

  .slo-product-image {
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}
</style>
